import React, { useState, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import Navigation from '../settings-navigation/Navigation'
import {
  CustomInputLabel,
  CustomTextField,
} from '../../components/restyled-mui/CustomInputs'
import {
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  FormControl,
  Tooltip,
  InputAdornment,
  FormHelperText,
  Autocomplete,
  Stack,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { makeStyles, useTheme } from '@mui/styles'
import { getSettingsStyles } from '../../styles/settingsStyles'
import { CustomButton } from '../../components/restyled-mui/CustomButton'
import {
  getAllUsers,
  createUser,
  deleteUser,
  updateUser,
} from '../../redux/actions/users'
import PermissionsSelection from './PermissionsSelection'
import SetupsSelections from './SetupsSelections'
import ConfirmationDialog from '../confirmation-dialog/Confirmation'
import ClearIcon from '@mui/icons-material/Clear'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import getModulePermissions from '../../utils/moduleConfig'
import Api from '../../api'
import timezones from '../../../src/utils/timezones'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { copyToClipboard, generatePassword, validatePassword } from './utils'
import UserLogsTable from './UserLogsTable'
import NoSite from '../../components/NoSite'
import { setSelectedUserSetup } from '../../redux/actions/userSetups'

const useStyles = makeStyles(theme => ({
  ...getSettingsStyles(theme),
  mainContainer: {
    width: '95%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    paddingTop: '10px',
  },
  setupsGrid: {
    paddingTop: '36px',
    marginLeft: '15%',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '4px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  switchContainer: {
    display: 'flex',
    marginLeft: 'auto',
  },
  sortableText: {
    minWidth: 120,
    cursor: 'pointer !important',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  userLogTable: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    height: 550, // <-- set a fixed height or adjust as needed
    width: '100%',
  },
  userLogMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4, 0),
    width: '100%',
  },
  toggleVisibility: {
    padding: '3px',
  },

  actionButtons: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2), // spacing between buttons
  },

  deleteButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.generalUi.borders}`,
    color: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.generalUi.panelBackground,
    },
  },

  cancelButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.generalUi.borders}`,
    color: theme.palette.text.default,
    '&:hover': {
      backgroundColor: theme.palette.generalUi.panelBackground,
    },
  },

  submitButton: {
    // you can keep color="primary" on the button, this is optional
  },
}))

const User = props => {
  const classes = useStyles(props)
  const theme = useTheme()
  const {
    history,
    loadedUser,
    users,
    getAllUsers,
    createUser,
    deleteUser,
    updateUser,
    creating,
    match,
    apiLoading,
    apiStatusOk,
  } = props
  const initialConfirmDialog = {
    open: false,
    confirmCallback: null,
    cancelCallback: null,
    title: '',
    content: '',
    color: '',
  }
  const units = ['kW', 'kWh', 'MW', 'MWh']
  const [confirmDialog, setConfirmDialog] = useState(initialConfirmDialog)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [allModulePermissions, setAllModulePremissions] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [allSetups, setAllSetups] = useState(null)
  const [searchSetupName, setSearchSetupName] = useState('')
  const [toNotDeleteYourselfOrAdmin, setToNotDeleteYourselfOrAdmin] =
    useState(false)
  const [setupsLoading, setSetupsLoading] = useState(true)
  const [formState, setFormState] = useState({
    username: '',
    usernameError: '',
    isUsernameAuto: true,
    email: '',
    emailError: '',
    password: '',
    passwordError: '',
    permissions: ['overview', 'history', 'metrics'],
    permissionsError: '',
    timezone: 'Etc/UTC',
    timezoneError: '',
    unit: 'MW',
    unitError: '',
    setups: [],
    setupsError: '',
    ...(creating ? {} : { enabled: false, enabledError: '' }), // Only include enabled in edit mode
    userSelectedSetups: [],
    selectedUserSetupsError: '',
  })

  const [showPassword, setShowPassword] = useState(false)
  //in case I need disable submit button
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const [sortMode, setSortMode] = useState('alphabetical')

  // USER LOGS
  const [userLogsLoading, setUserLogsLoading] = useState(true)
  const [userLogs, setUserLogs] = useState([])
  const [selectedUserLogs, setSelectedUserLogs] = useState(null)
  const [userLogsError, setUserLogsError] = useState(null)

  const dispatch = useDispatch()

  useEffect(() => {
    const fetchAllSetups = async () => {
      try {
        setSetupsLoading(true)
        const response = await Api.Setups.getAllSetups()

        /**
         This is all existing setups
         */
        const allSetups = Array.isArray(response.data.setups)
          ? response.data.setups
          : []

        setAllSetups(allSetups)
      } catch (error) {
        console.error('Error fetching client setups:', error)
      } finally {
        setSetupsLoading(false)
      }
    }

    fetchAllSetups()
  }, [loadedUser])

  /**
   * API call to fetch user logs.
   * Should be triggered in parallel once the user is loaded.
   */
  useEffect(() => {
    const fetchUserLogs = async () => {
      setUserLogsError(null)

      if (!selectedUserLogs || !selectedUserLogs.id) {
        setUserLogs([])
        setUserLogsLoading(false)
        return
      }
      try {
        setUserLogsLoading(true)
        const response = await Api.Users.userLogRequest({
          user_id: selectedUserLogs.id,
        })

        if (response.status === 200) {
          if (response.data.logs.length > 0) {
            setUserLogs(response.data.logs)
            setUserLogsError(null)
          } else {
            setUserLogs([])
            setUserLogsError(
              `No logs found for this user: ${selectedUserLogs.name}`,
            )
          }
        } else {
          setUserLogs([])
          setUserLogsError('An unexpected error occurred.')
        }
      } catch (err) {
        setUserLogs([])
        setUserLogsError('Failed to fetch user logs.')
        console.error('Error fetching user logs:', err)
      } finally {
        setUserLogsLoading(false)
      }
    }
    fetchUserLogs()
  }, [selectedUserLogs])

  useEffect(() => {
    if (!users) {
      getAllUsers()
    }
  }, [users, getAllUsers])

  useEffect(() => {
    if (!allModulePermissions) {
      setAllModulePremissions(getModulePermissions())
    }
  }, [allModulePermissions])

  useEffect(() => {
    if (users) {
      if (creating) {
        setUser({
          name: '',
          email: '',
          password: '',
          permissions: ['overview', 'history', 'metrics'],
          setups: [],
        })
        setLoading(false)
      } else {
        const userSelected = users.find(u => u.id === parseInt(match.params.id))
        if (userSelected && loadedUser && allSetups) {
          if (userSelected.id === loadedUser?.userId || userSelected.id === 1) {
            setToNotDeleteYourselfOrAdmin(true)
          } else {
            setToNotDeleteYourselfOrAdmin(false)
          }

          // need this to chaing the API call for user logs
          setSelectedUserLogs(userSelected)

          const selectedSetups = allSetups
            .filter(
              setup =>
                setup.enabled && userSelected.setups.includes(setup.name),
            )
            .map(setup => setup.name)

          const shouldShowError =
            !userSelected.enabled && selectedSetups.length === 0

          setFormState({
            ...formState,
            username: userSelected.name,
            email: userSelected.email,
            permissions: userSelected.permissions,
            password: '',
            timezone:
              userSelected.id === loadedUser?.userId
                ? loadedUser.timezone
                : userSelected.timezone,
            unit:
              userSelected.id === loadedUser?.userId
                ? loadedUser.unit
                : userSelected.unit,
            setups: userSelected.setups,
            enabled: userSelected.enabled,
            userSelectedSetups: selectedSetups,
            enabledError: shouldShowError
              ? 'Select at least one setup to enable the user'
              : '',
          })

          setUser(userSelected)
          setLoading(false)
        }
      }
    }
  }, [users, creating, match.params.id, allSetups, loadedUser])

  useEffect(() => {
    if (!apiLoading && apiStatusOk && submitted) {
      history.push(`/settings/users`)
      setSubmitted(false)
    }
  }, [apiLoading, submitted, apiStatusOk, history])

  /* <--------------- validations ---------------> */

  const validateUsername = name => {
    if (!name.trim()) {
      return 'Username cannot be empty'
    }
    if (!/^[a-zA-Z0-9_.]+$/.test(name)) {
      return 'Username can only contain letters, numbers, points and underscore (_)'
    }
    if (
      creating &&
      users.some(s => s.name.toLowerCase() === name.toLowerCase())
    ) {
      return 'This username is already in use'
    }
    return ''
  }

  const validateEmail = email => {
    const trimmed = email.trim()
    if (!trimmed) {
      return 'Email cannot be empty'
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(trimmed)) {
      return 'Invalid email format'
    }
    return ''
  }

  const passwordValidation = pass => {
    if (creating && !pass.trim()) {
      return 'Password cannot be empty'
    }

    if (pass.trim()) {
      const result = validatePassword(pass)
      return result.valid ? '' : result.errors[0] || 'Weak password'
    }

    return ''
  }

  const validateTimezone = value => {
    if (!value) {
      return 'Timezone is required'
    }
    return ''
  }

  const validateUnit = value => {
    if (!value) {
      return 'Unit is required'
    }
    return ''
  }

  const validatePermissions = arr => {
    if (arr.length === 0) {
      return 'User must have at least one permission'
    }
    return ''
  }

  const validateSetups = arr => {
    if (arr.length === 0) {
      return 'User requires at least one setup'
    }
    return ''
  }

  /** handlers */

  const handleTextInputChange = field => e => {
    const newVal = e.target.value

    let error = ''
    if (field === 'username') {
      error = validateUsername(newVal)
    } else if (field === 'email') {
      error = validateEmail(newVal)
    } else if (field === 'password') {
      error = passwordValidation(newVal)
    }

    setFormState({
      ...formState,
      [field]: newVal,
      ...(field === 'username' && {
        usernameError: error,
        isUsernameAuto: false,
      }),
      ...(field === 'email' && { emailError: error }),
      ...(field === 'password' && { passwordError: error }),
    })
  }

  const handleAutocompleteChange = field => (_, newValue) => {
    let error = ''
    if (field === 'timezone') {
      error = validateTimezone(newValue)
    } else if (field === 'unit') {
      error = validateUnit(newValue)
    }

    setFormState({
      ...formState,
      [field]: newValue,
      ...(field === 'timezone' && { timezoneError: error }),
      ...(field === 'unit' && { unitError: error }),
    })
  }

  const validateForm = () => {
    const errors = {}

    const usernameError = validateUsername(formState.username)
    if (usernameError) {
      errors.usernameError = usernameError
    }

    const emailError = validateEmail(formState.email)
    if (emailError) {
      errors.emailError = emailError
    }

    const passwordError = passwordValidation(formState.password)
    if (passwordError) {
      errors.passwordError = passwordError
    }

    const timezoneError = validateTimezone(formState.timezone)
    if (timezoneError) {
      errors.timezoneError = timezoneError
    }

    const unitError = validateUnit(formState.unit)
    if (unitError) {
      errors.unitError = unitError
    }

    const permissionsError = validatePermissions(formState.permissions)
    if (permissionsError) {
      errors.permissionsError = permissionsError
    }

    // For create mode, validate setups array
    if (creating) {
      const setupsError = validateSetups(formState.setups)
      if (setupsError) {
        errors.setupsError = setupsError
      }
    }
    // For edit mode, validate userSelectedSetups
    else {
      if (formState.userSelectedSetups.length === 0) {
        errors.selectedUserSetupsError =
          'User requires at least one selected setup'
      }

      // If user is enabled but has no selected setups
      if (formState.enabled && formState.userSelectedSetups.length === 0) {
        errors.enabledError = 'Cannot enable user without selected setups'
      }
    }

    return errors
  }

  const isFormValid = useMemo(() => {
    // If the user hasn't attempted to submit yet, we only validate setups and enabled state
    if (!attemptedSubmit) {
      // Check if user is enabled but has no selected setups
      if (
        !creating &&
        formState.enabled &&
        formState.userSelectedSetups.length === 0
      ) {
        return false
      }
      return true
    }

    // After attempted submit, validate all fields
    const errors = validateForm()
    return Object.keys(errors).length === 0
  }, [formState, attemptedSubmit, creating])

  const handleCreate = () => {
    setAttemptedSubmit(true)
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({ ...prev, ...errors }))
      return
    }

    const newUser = {
      ...user,
      name: formState.username,
      email: formState.email,
      password: formState.password,
      timezone: formState.timezone,
      unit: formState.unit,
      enabled: true,
    }

    // reset poteintial selected user to unselected
    dispatch(setSelectedUserSetup(null))
    createUser(newUser)
    setSubmitted(true)
  }

  const handleSubmit = () => {
    setAttemptedSubmit(true)
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({ ...prev, ...errors }))
      return
    }

    const { password, ...restUser } = user
    const updUser = {
      ...restUser,
      email: formState.email,
      ...(formState.password !== '' && { password: formState.password }),
      timezone: formState.timezone,
      unit: formState.unit,
      enabled: formState.enabled,
    }

    // reset poteintial selected user to unselected
    dispatch(setSelectedUserSetup(null))
    updateUser(updUser)
    setSubmitted(true)
  }

  const handleCancel = () => {
    history.push(`/settings/users`)
  }

  const handleDelete = () => {
    setConfirmDialog({
      open: true,
      confirmCallback: () => {
        deleteTheUser(user)
        setConfirmDialog(initialConfirmDialog)
      },
      cancelCallback: () => setConfirmDialog(initialConfirmDialog),
      title: 'Delete User',
      content: (
        <Typography>
          Are you sure you want to delete <b>{user.name}</b>?
        </Typography>
      ),
      color: theme.palette.error.main,
      type: 'warning',
    })
  }

  const deleteTheUser = user => {
    deleteUser(user.id, user.name)
    setSubmitted(true)
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  // const handleToggleUser = () => {
  //   // If trying to enable user but no setups are selected
  //   if (!formState.enabled && formState.userSelectedSetups.length === 0) {
  //     setFormState(prev => ({
  //       ...prev,
  //       enabledError: 'Select at least one setup to enable the user',
  //     }))
  //     return
  //   }

  //   if (formState.enabled && formState.userSelectedSetups.length === 0) {
  //     setFormState(prev => ({
  //       ...prev,
  //       enabled: false,
  //       enabledError: 'Select at least one setup to enable the user',
  //     }))
  //     return
  //   }

  //   // Toggle enabled state and clear any error
  //   setFormState(prev => ({
  //     ...prev,
  //     enabled: !prev.enabled,
  //     enabledError: '',
  //   }))
  // }

  const getUsernameFromEmail = email => {
    const trimmed = email.trim()
    const at = trimmed.indexOf('@')
    if (at <= 0) return ''
    return trimmed.slice(0, at)
  }

  /**
   * Auto-update username from email initialy,
   * if the user edits username once, it won’t keep changing as they type email
   */
  useEffect(() => {
    if (!creating) return

    setFormState(prev => {
      const suggested = getUsernameFromEmail(prev.email)
      if (!suggested) return prev

      // only fill if empty OR it was auto-filled before
      if (prev.username.trim() !== '' && !prev.isUsernameAuto) return prev

      const nextUsernameError = validateUsername(suggested)

      return {
        ...prev,
        username: suggested,
        usernameError: nextUsernameError,
        isUsernameAuto: true,
      }
    })
  }, [creating, formState.email])

  useEffect(() => {
    if (creating) return // enabled is only in edit mode per your formState init

    const shouldEnable = formState.userSelectedSetups.length > 0

    setFormState(prev => ({
      ...prev,
      enabled: shouldEnable,
      enabledError: shouldEnable
        ? ''
        : 'Select at least one setup to enable the user',
    }))
  }, [creating, formState.userSelectedSetups.length])

  const renderForm = () => {
    return (
      <Grid container spacing={4} className={classes.formContainer}>
        <Grid item xs={12} md={12} lg={12} style={{ display: 'flex' }}>
          <Grid item xs={12} md={6} lg={4}>
            <>
              <CustomInputLabel>Email</CustomInputLabel>
              <CustomTextField
                disabled={creating ? false : true}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                type="text"
                onChange={handleTextInputChange('email')}
                value={formState.email}
                error={Boolean(formState.emailError)}
                helperText={formState.emailError}
                InputProps={{
                  endAdornment: creating && (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label="copy email"
                            onClick={() => copyToClipboard(formState.email)}
                            disabled={!formState.email}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </>

            {creating && (
              <>
                <CustomInputLabel>Username</CustomInputLabel>
                <CustomTextField
                  variant="outlined"
                  margin="dense"
                  size="small"
                  fullWidth
                  type="text"
                  onChange={handleTextInputChange('username')}
                  value={formState.username}
                  error={Boolean(formState.usernameError)}
                  helperText={formState.usernameError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label="copy username"
                              onClick={() =>
                                copyToClipboard(formState.username)
                              }
                              disabled={!formState.username}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <>
              {creating ? (
                <CustomInputLabel>Password</CustomInputLabel>
              ) : (
                <div className={classes.tooltipContainer}>
                  <CustomInputLabel>Change password</CustomInputLabel>
                  <Tooltip
                    title="The password will remain unchanged if the field is left empty."
                    placement="top"
                    arrow
                    classes={{
                      tooltip: classes.tooltip,
                      arrow: classes.arrow,
                    }}
                  >
                    <span>
                      <IconButton
                        style={{ padding: '0' }}
                        aria-label="info"
                        disableTouchRipple
                      >
                        <HelpIcon
                          fontSize="small"
                          className={classes.helpIcon}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              )}
              <CustomTextField
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                onChange={handleTextInputChange('password')}
                value={formState.password}
                error={Boolean(formState.passwordError)}
                helperText={formState.passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* Generate / Regenerate */}
                      <Tooltip title="Generate password">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            const pw = generatePassword(16)
                            // update your state however you do it
                            // example:
                            setFormState(prev => ({
                              ...prev,
                              password: pw,
                              passwordError: '',
                            }))
                          }}
                          aria-label="generate password"
                          style={{ marginRight: '5px' }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Copy */}
                      <Tooltip title="Copy">
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => copyToClipboard(formState.password)}
                            aria-label="copy password"
                            disabled={!formState.password}
                            style={{ marginRight: '2px' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Visibility">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? (
                            <VisibilityOff
                              className={classes.toggleVisibility}
                              fontSize="small"
                            />
                          ) : (
                            <Visibility
                              className={classes.toggleVisibility}
                              fontSize="small"
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </>

            {creating ? (
              <Accordion
                defaultExpanded={false}
                sx={{
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.generalUi.borders}`,
                  '&:before': { display: 'none' },
                  marginTop: 1,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 40,
                    '& .MuiAccordionSummary-content': { margin: '8px 0' },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: theme.palette.primary.main,
                      textTransform: 'uppercase',
                    }}
                  >
                    Permissions
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '0 16px 16px' }}>
                  {allModulePermissions ? (
                    <>
                      <PermissionsSelection
                        allPermissions={allModulePermissions}
                        user={user}
                        setUser={setUser}
                        setFormState={setFormState}
                      />
                      <FormHelperText
                        style={{ color: '#C63C3C' }}
                        id="setup_permission"
                      >
                        {formState.permissionsError}
                      </FormHelperText>
                    </>
                  ) : (
                    <div style={{ margin: '25% 45%' }}>
                      <CircularProgress />
                    </div>
                  )}
                </AccordionDetails>
              </Accordion>
            ) : (
              <>
                <CustomInputLabel>Permissions</CustomInputLabel>
                {allModulePermissions ? (
                  <>
                    <PermissionsSelection
                      allPermissions={allModulePermissions}
                      user={user}
                      setUser={setUser}
                      setFormState={setFormState}
                    />
                    <FormHelperText
                      style={{ color: '#C63C3C' }}
                      id="setup_permission"
                    >
                      {formState.permissionsError}
                    </FormHelperText>
                  </>
                ) : (
                  <div style={{ margin: '25% 45%' }}>
                    <CircularProgress />
                  </div>
                )}
              </>
            )}
            <>
              <CustomInputLabel>Timezone</CustomInputLabel>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  variant="outlined"
                  size="small"
                  options={timezones}
                  value={formState.timezone}
                  autoHighlight
                  onChange={handleAutocompleteChange('timezone')}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                      error={Boolean(formState.timezoneError)}
                      helperText={formState.timezoneError}
                    />
                  )}
                />
              </FormControl>
            </>

            <>
              <CustomInputLabel>Unit</CustomInputLabel>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  variant="outlined"
                  size="small"
                  options={units}
                  value={formState.unit}
                  autoHighlight
                  onChange={handleAutocompleteChange('unit')}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                      error={Boolean(formState.unitError)}
                      helperText={formState.unitError}
                    />
                  )}
                />
              </FormControl>
            </>
          </Grid>

          <Grid item xs={12} md={6} lg={4} className={classes.setupsGrid}>
            <CustomInputLabel>Setups</CustomInputLabel>
            <CustomTextField
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              type="setups"
              placeholder="Search for setup..."
              autoComplete="on"
              onChange={e => setSearchSetupName(e.target.value)}
              value={searchSetupName}
              InputProps={{
                endAdornment: searchSetupName && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchSetupName('')
                    }}
                    aria-label="Clear All"
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />

            {allSetups ? (
              <>
                <SetupsSelections
                  allSetups={allSetups}
                  user={user}
                  setUser={setUser}
                  searchSetupName={searchSetupName}
                  setFormState={setFormState}
                  creating={creating}
                  sortMode={sortMode}
                ></SetupsSelections>
                <FormHelperText
                  style={{ color: '#C63C3C' }}
                  id="setup_users_selection"
                >
                  {formState.setupsError}
                </FormHelperText>
                <Stack spacing={0.5}>
                  {user?.setups && formState.userSelectedSetups && (
                    <>
                      <Stack spacing={0.25} pl={2}>
                        <Stack direction="row" spacing={1}>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ minWidth: 120 }}
                          >
                            Sorting mode:
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            className={classes.sortableText}
                            onClick={() =>
                              setSortMode(prev =>
                                prev === 'alphabetical'
                                  ? 'selected'
                                  : 'alphabetical',
                              )
                            }
                          >
                            {sortMode === 'alphabetical'
                              ? 'Alphabetical'
                              : 'Selected first'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Typography
                            variant="caption"
                            color={
                              (!creating || attemptedSubmit) &&
                              formState.userSelectedSetups.length === 0
                                ? 'error'
                                : 'textSecondary'
                            }
                            style={{ minWidth: 120 }}
                          >
                            Selected Setups:
                          </Typography>
                          <Typography
                            variant="caption"
                            color={
                              (!creating || attemptedSubmit) &&
                              formState.userSelectedSetups.length === 0
                                ? 'error'
                                : 'textSecondary'
                            }
                          >
                            {formState.userSelectedSetups.length}
                          </Typography>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Stack>
              </>
            ) : (
              <div style={{ margin: '25% 45%' }}>
                <CircularProgress />
              </div>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <div
            className={
              userLogsLoading || userLogsError
                ? classes.userLogMessage
                : classes.userLogTable
            }
          >
            {userLogsLoading ? (
              <CircularProgress />
            ) : userLogsError ? (
              <NoSite heading="No Logs Available" body={userLogsError} />
            ) : (
              <UserLogsTable userLogs={userLogs} user={selectedUserLogs} />
            )}
          </div>
        </Grid>
      </Grid>
    )
  }

  const renderActionButtons = () => {
    return (
      <div className={classes.actionButtons}>
        {!creating && (
          <CustomButton
            onClick={handleDelete}
            disabled={apiLoading || toNotDeleteYourselfOrAdmin}
            variant="contained"
            disableElevation
            className={classes.deleteButton}
          >
            Delete
          </CustomButton>
        )}
        <CustomButton
          onClick={handleCancel}
          disabled={apiLoading}
          variant="contained"
          disableElevation
          className={classes.cancelButton}
        >
          Cancel
        </CustomButton>

        <CustomButton
          onClick={creating ? handleCreate : handleSubmit}
          disabled={!isFormValid || apiLoading}
          variant="contained"
          disableElevation
          color="primary"
          className={classes.submitButton}
        >
          {apiLoading ? <CircularProgress size={14} /> : 'Submit'}
        </CustomButton>
      </div>
    )
  }

  return (
    <Navigation history={history}>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </div>
      ) : user ? (
        <>
          <ConfirmationDialog {...confirmDialog} />
          <div className={classes.mainContainer}>
            <div className={classes.headerContainer}>
              <>
                <Typography variant="h2" className={classes.title}>
                  {creating ? 'New User' : user.name}
                </Typography>

                {!toNotDeleteYourselfOrAdmin &&
                !creating &&
                !setupsLoading &&
                allSetups ? (
                  <div className={classes.switchContainer}>
                    {/* <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" style={{ marginRight: 12 }}>
                        Activate
                      </Typography>

                      <Switch
                        checked={formState.enabled}
                        onChange={handleToggleUser}
                        disabled={formState.userSelectedSetups.length === 0}
                        color="primary"
                        size="small"
                      />
                    </div> */}

                    {formState.userSelectedSetups.length === 0 && (
                      <FormHelperText
                        error
                        sx={{ pl: 2 }}
                        style={{ fontSize: '1rem' }}
                      >
                        Select at least one setup to enable the user
                      </FormHelperText>
                    )}
                  </div>
                ) : null}
              </>
              {renderActionButtons()}
            </div>
            {renderForm()}
          </div>
        </>
      ) : (
        <Typography variant="h2">User not found</Typography>
      )}
    </Navigation>
  )
}

const mapStateToProps = state => {
  return {
    loadedUser: state.user,
    users: state.users,
    apiLoading: state.status.loading,
    apiStatusOk: state.status.snackbar.severity,
    userPermissions: state.userPermissions.permissions,
  }
}

export default connect(mapStateToProps, {
  getAllUsers,
  createUser,
  deleteUser,
  updateUser,
})(User)
